
import * as Enumerable from 'linq';
import { Observable, Subject } from 'rxjs';
import { ActivityEntityName } from '../../../entities';
import { SecurityModuleName } from '../../../security';
import { MetadataEntityData, MetadataEntityFieldData, MetadataEntityFieldType } from '../decorators';
import { MetadataImportConfig } from './metadata.import.config';
import { MetadataImportFieldBuilder } from './metadata.import.field.builder';
import { metadataImportCellValidator } from './metadata.import.validators';
import { MetadataObjectBuilder } from './metadata.object.builder';

interface MetadataValidationComponent {
    change$: Observable<void>;
    changed(): void;
    parent: MetadataValidationComponent;
}

export enum BulkRowAction {
    None = 'None',
    Exclude = 'Exclude',
    Include = 'Include'
}

export class MetadataImportValidationCell implements MetadataValidationComponent {

    private _change = new Subject<void>();
    public readonly change$ = this._change.asObservable();

    private _value: any;
    public get value(): any {
        return this._value;
    }

    public set value(value: any) {
        if (this?.field?.type === MetadataEntityFieldType._Action_) {
            // For the first time set the value, we don't need to validate
            if (this._value && this._value !== value) {
                this._value = value;
                this.changed();
            }
            else {
                this._value = value;
            }

            return;
        }

        this._value = value;
        this.changed();
    }

    private _isValid = true;
    public get isValid() {
        return this._isValid;
    }

    private _message: string;
    public get message() {
        return this._message;
    }

    public parent: MetadataValidationComponent;

    constructor(
        value: any,
        private field: MetadataEntityFieldData,
        private config: MetadataImportConfig
    ) {
        this.value = value;

        if (!field)
            console.log('Field Not Set', this.value);
    }

    public changed(): void {
        // if (this?.field?.type === MetadataEntityFieldType._Action_)
        //     return;

        const result = metadataImportCellValidator(this.field, this.value, this.config);
        this._value = result.value;
        this._isValid = result.success;
        this._message = result.message;

        this?.parent?.changed();
        this._change.next();
    }
}

export class MetadataImportValidationRow implements MetadataValidationComponent {

    private _change = new Subject<void>();
    public readonly change$ = this._change.asObservable();

    public get action(): BulkRowAction {
        return this.cells[0].value;
    }

    public get isValid() {
        if (this.action === BulkRowAction.Exclude) return true;

        return Enumerable.from(this.cells).all(x => x.isValid);
    }

    public parent: MetadataValidationComponent;

    constructor(
        public readonly cells: MetadataImportValidationCell[],
    ) {
        Enumerable
            .from(cells)
            .forEach(x => x.parent = this);
    }

    public setValue(index: number, value: any) {
        if (index > this.cells.length) return;
        this.cells[index].value = value;
    }

    public getValue(index: number): any {
        if (index > this.cells.length) return;
        return this.cells[index].value;
    }

    public changed(): void {
        this?.parent?.changed();
        this._change.next();
    }
}

export class MetadataImportValidationTransportData {

    constructor(data: MetadataImportValidationData) {

        this.name = data.name;
        this.entity = data.entity;
        this.module = data.module;
        this.config = data.config;

        this.headers = Enumerable
            .from(data.headers)
            .select(x => x.path)
            .toArray();

        this.rows = Enumerable
            .from(data?.rows)
            .where(row => [BulkRowAction.Include].includes(row.action))
            .select(row => {
                const rowWithData = Enumerable
                    .from(row.cells)
                    .select(x => x.value)
                    .toArray();

                return rowWithData;
            })
            .toArray();

    }

    name: string;
    module: SecurityModuleName;
    entity: ActivityEntityName;
    headers: string[];
    config: MetadataImportConfig;
    rows: any[][];
}

/**
 * @class MetadataImportValidationData
 * @description This class is used to hold the mapped data coming from the import file. Like the file it consists of rows and cells. The headers contain the mapping to the
 * WorkBuddy fields.
 * 
 * This class will contain utility methods and properties to used validate the data.
 */
export class MetadataImportValidationData implements MetadataValidationComponent {

    private _change = new Subject<void>();
    public readonly change$ = this._change.asObservable();

    private readonly _rows: MetadataImportValidationRow[] = [];
    public get rows(): ReadonlyArray<MetadataImportValidationRow> {
        return this._rows;
    }

    public get invalidRowCount(): number {
        const count = Enumerable
            .from(this.rows)
            .count(x => !x.isValid);

        return count;
    }


    public get isValid() {
        return Enumerable.from(this.rows).all(x => x.isValid);
    }

    public parent: MetadataValidationComponent;

    constructor(
        public readonly name: string,
        public readonly module: SecurityModuleName,
        public readonly entity: ActivityEntityName,
        public readonly headers: MetadataEntityFieldData[],
        public readonly config?: MetadataImportConfig
    ) { }

    changed(): void {
        this._change.next();
    }

    public addRow(row: MetadataImportValidationRow) {
        row.parent = this;
        this._rows.push(row);
    }

    /**
     * @description This method will return a distinct list of values in all rows for a cell at a given index.
     * @param cellIndex The index of the cell in the row
     * @returns The distinct list of values
     */
    public getDistinctValues(cellIndex: number): any[] {
        const values = Enumerable
            .from(this.rows)
            .select(x => x.getValue(cellIndex))
            .distinct()
            .toArray();

        return values;
    }

    /**
     * @description This method converts all of the rows into an object with the shape of the object defined by the metadata.
     * @returns Returns an array of objects with the shape of the object defined by the metadata. Each item in the array is a row of data.
     */
    public toObject() {
        const data = [];

        Enumerable
            .from(this.rows)
            .forEach((row, rowIndex) => {

                let rowData = {};

                Enumerable
                    .from(this.headers)
                    .forEach((header, headerIndex) => {
                        MetadataObjectBuilder.set(rowData, header?.path, row.cells[headerIndex].value);
                    });

                data.push(rowData);
            });

        return data;
    }

    /**
     * @description This method is used to serialize the data to a transport object. Ideally this should be used to send the data to the server.
     */
    public serialize(): MetadataImportValidationTransportData {
        return new MetadataImportValidationTransportData(this);
    }

    /**
     * @description This method is used to deserialize the data from a transport object. Ideally this should be used to receive the data on the server.
     * @param data 
     * @returns A reconstructed MetadataImportValidationData object.
     */
    public static deserialize(data: MetadataImportValidationTransportData, metadataEntity: MetadataEntityData): MetadataImportValidationData {

        const fields = MetadataImportFieldBuilder.getAvailableFields(metadataEntity);

        // Build a map of the available fields
        const fieldMap = Enumerable.from(fields).toDictionary(x => x.path, x => x);

        // Get metadata fields from string to object
        const headers = Enumerable
            .from(data?.headers)
            .select(x => fieldMap.get(x))
            .toArray();

        // Reconstruction the action cell
        const actionHeader = {
            type: MetadataEntityFieldType._Action_,
            name: MetadataEntityFieldType._Action_
        } as MetadataEntityFieldData;

        headers[0] = actionHeader;

        const validator = new MetadataImportValidationData(data.name, data?.module, data?.entity, headers);

        Enumerable
            .from(data?.rows)
            .forEach(row => {

                const cells = Enumerable
                    .from(row)
                    .select((cell, i) => {
                        return new MetadataImportValidationCell(cell, headers[i], data?.config);
                    })
                    .toArray();

                const metadataRow = new MetadataImportValidationRow(cells);
                validator.addRow(metadataRow);
            });

        return validator;

    }

}